import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { findIndex } from 'lodash'

import { CommonTableColumnsEnum } from '../components/Table/types'
import { showToast } from '../components/Toasts'
import { CURRENT_ACCOUNT_ID } from '../constants'
import i18n from '../i18n'
import { fetchCashFlowBalance, fetchCashFlows } from '../pages/CashFlow/CashFlowSlice'
import {
  fetchClosedPositionsData,
  fetchDashboardWidgetsData,
  fetchOpenPositionsData,
} from '../pages/Dashboard/DashboardSlice'
import { createPersistMiddleware } from '../store/createPersistMiddleware'
import { RootState } from '../store/reducer'
import { LoadingStatusEnum } from '../store/types'
import { generateAutoUploadLSKey } from '../utils'

import {
  AutoUploadStatusEnum,
  AutoUploadTriggerEnum,
  UserAccount,
  UserAccountsInitialStateType,
} from './types'
import { UserService } from './UserService'

const initialState: UserAccountsInitialStateType = {
  userAccounts: [],
  currentAccount: null,
  period: [null, null],
  lastAccountUpdate: undefined,
  isRunningAutoUpload: false,
  isHydrated: false,
  loading: LoadingStatusEnum.Idle,
  accountSyncDateLoading: LoadingStatusEnum.Idle,
}

export const fetchUserAccounts = createAsyncThunk('userAccounts/fetchUserAccounts', async () => {
  try {
    const { data: userAccounts } = await UserService.getUserAccounts()
    return userAccounts
  } catch (error) {
    throw Error(error.message)
  }
})

export const fetchLastAccountDate = createAsyncThunk(
  'userAccounts/fetchLastAccountDate',
  async (accountId: number) => {
    try {
      const {
        data: { date },
      } = await UserService.fetchLastAccountDate(accountId)
      return date
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const runAutoUploadProcess = createAsyncThunk(
  'userAccounts/runAutoUploadProcess',
  async (accountId: number) => {
    const t = i18n.t
    try {
      localStorage.setItem(generateAutoUploadLSKey(accountId), AutoUploadTriggerEnum.true)
      const { success } = await UserService.refreshAccountData(accountId)
      showToast.success(
        t('layout.header.toasts.dataImportedSuccessfully', 'Data has been imported successfully'),
      )
      localStorage.removeItem(generateAutoUploadLSKey(accountId))
      return success
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const getAutoUploadStatus = createAsyncThunk(
  'userAccounts/getAutoUploadStatus',
  async (accountId: number) => {
    try {
      const {
        data: { status },
      } = await UserService.getAutoUploadStatus(accountId)

      if (status === AutoUploadStatusEnum.success) {
        localStorage.setItem(generateAutoUploadLSKey(accountId), AutoUploadTriggerEnum.false)
      }

      return status
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const runAutoUploadProcessRefreshData = createAsyncThunk<void, number, { state: RootState }>(
  `userAccounts/runAutoUploadProcessRefreshData`,
  async (accountId, { dispatch, getState }) => {
    try {
      const result = await dispatch(runAutoUploadProcess(accountId))

      const currentId = getState().userAccounts.currentAccount?.id

      if (runAutoUploadProcess.fulfilled.match(result) && currentId === accountId) {
        await dispatch(refreshAccountTradingData(accountId))
      }
    } catch (error) {
      throw Error(error.message)
    }
  },
)

export const refreshAccountTradingData = createAsyncThunk<void, number, { state: RootState }>(
  'userAccounts/refreshAccountTradingData',
  async (accountId, { dispatch, getState }) => {
    const state = getState()
    const currentId = state.userAccounts.currentAccount?.id

    dispatch(fetchUserAccounts())

    if (currentId !== accountId) {
      return
    }

    const { period } = state.userAccounts
    const openPagination = state.dashboard.positions.open.pagination
    const closedPagination = state.dashboard.positions.closed.pagination
    const cashFlowPagination = state.cashFlow.pagination

    await Promise.all([
      dispatch(fetchLastAccountDate(accountId)),
      dispatch(fetchDashboardWidgetsData({ account: accountId, period })),
      dispatch(
        fetchOpenPositionsData({
          order: openPagination.order,
          orderBy: openPagination.orderBy || CommonTableColumnsEnum.id,
          page: openPagination.page,
          perPage: openPagination.perPage,
          period,
          account: accountId,
        }),
      ),
      dispatch(
        fetchClosedPositionsData({
          order: closedPagination.order,
          orderBy: closedPagination.orderBy || CommonTableColumnsEnum.id,
          page: closedPagination.page,
          perPage: closedPagination.perPage,
          period,
          account: accountId,
        }),
      ),
      dispatch(
        fetchCashFlows({
          order: cashFlowPagination.order,
          orderBy: cashFlowPagination.orderBy || CommonTableColumnsEnum.date,
          page: cashFlowPagination.page,
          perPage: cashFlowPagination.perPage,
          period,
          account: accountId,
        }),
      ),
      dispatch(fetchCashFlowBalance({ account: accountId })),
    ])
  },
)

// We need that only bcs localstorage key should have userId
export const restoreCurrentAccount = createAsyncThunk(
  'userAccounts/restoreCurrentAccount',
  async (userId: string) => {
    const key = userId ? `${CURRENT_ACCOUNT_ID}_${userId}` : null

    if (!key) return null

    const currentAccountId = localStorage.getItem(key)
    return currentAccountId ? JSON.parse(currentAccountId) : null
  },
)

const UserAccountsSlice = createSlice({
  name: 'UserAccounts',
  initialState,
  reducers: {
    setCurrentAccount: (state, action) => {
      state.currentAccount = action.payload
    },
    editAccount: (state, action) => {
      const changedAccount: UserAccount = action.payload
      const index = findIndex(state.userAccounts, { id: changedAccount.id })
      if (index !== -1) {
        state.userAccounts[index] = changedAccount
      }
    },
    addAccount: (state, action) => {
      state.userAccounts = [...state.userAccounts, action.payload]
    },
    deleteAccount: (state, action) => {
      state.userAccounts = state.userAccounts.filter(({ id }) => id !== action.payload)
      state.currentAccount = state.userAccounts[0]
    },
    setPeriod: (state, action) => {
      state.period = action.payload
    },
    clearAllAccount: (state) => {
      state.userAccounts = initialState.userAccounts
      state.currentAccount = initialState.currentAccount
      state.period = initialState.period
      state.isHydrated = false
    },
    setIsRunningAutoUpload: (state) => {
      state.isRunningAutoUpload = true
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserAccounts.fulfilled, (state, action) => {
      state.userAccounts = action.payload

      if (state.currentAccount?.id) {
        const matched = action.payload.find((acc) => acc.id === state.currentAccount?.id)

        if (matched) {
          state.currentAccount = matched
        } else if (state.isHydrated) {
          state.currentAccount = action.payload[0] ?? null
        }
      }
    })
    builder.addCase(restoreCurrentAccount.pending, (state) => {
      state.loading = LoadingStatusEnum.Pending
    })
    builder.addCase(restoreCurrentAccount.fulfilled, (state, action) => {
      const currentAccountId = action.payload
      const matched = currentAccountId
        ? state.userAccounts.find((acc) => acc.id === currentAccountId)
        : undefined

      state.currentAccount = matched ?? state.userAccounts[0] ?? null
      state.isHydrated = true
      state.loading = LoadingStatusEnum.Succeeded
    })
    builder.addCase(restoreCurrentAccount.rejected, (state) => {
      state.loading = LoadingStatusEnum.Failed
    })
    builder.addCase(fetchLastAccountDate.pending, (state) => {
      state.accountSyncDateLoading = LoadingStatusEnum.Pending
    })
    builder.addCase(fetchLastAccountDate.fulfilled, (state, action) => {
      state.lastAccountUpdate = action.payload
      state.accountSyncDateLoading = LoadingStatusEnum.Succeeded
    })
    builder.addCase(fetchLastAccountDate.rejected, (state) => {
      state.accountSyncDateLoading = LoadingStatusEnum.Failed
    })
    builder.addCase(runAutoUploadProcess.pending, (state) => {
      state.isRunningAutoUpload = true
    })
    builder.addCase(runAutoUploadProcess.fulfilled, (state) => {
      state.isRunningAutoUpload = false
    })
    builder.addCase(runAutoUploadProcess.rejected, (state) => {
      state.isRunningAutoUpload = false
    })

    builder.addCase(getAutoUploadStatus.fulfilled, (state, action) => {
      state.isRunningAutoUpload = action.payload === AutoUploadStatusEnum.pending
    })
  },
})

const UserAccountsReducer = UserAccountsSlice.reducer
export const {
  setCurrentAccount,
  editAccount,
  addAccount,
  deleteAccount,
  setPeriod,
  clearAllAccount,
  setIsRunningAutoUpload,
} = UserAccountsSlice.actions

export const persistCurrentAccountMiddleware = createPersistMiddleware({
  selector: (state) => state.userAccounts.currentAccount?.id,
  isHydratedSelector: (state) => state.userAccounts.isHydrated,
  perUser: true,
  storageKey: CURRENT_ACCOUNT_ID,
})

export default UserAccountsReducer
